// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Injectable } from '@angular/core';
import { API_URL } from 'src/shared/api';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {IEvent, IEventSearchRequest, IEventSearchResponse, SEVERITY} from 'src/types/events';
import { Observable, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';
import {ConfigurationService} from './configuration-service';

@Injectable({providedIn: 'root'})
export class EventsService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private configurationService: ConfigurationService,
  ) { }

  getUrl(suffix: string) {
    return `${this.configurationService.apiUrl}${suffix}`;
  }

  info(event: IEvent): Observable<void> {
    event.severity = SEVERITY.INFO;
    return this.postEvent(event);
  }

  error(event: IEvent): Observable<void> {
    event.severity = SEVERITY.ERROR;
    return this.postEvent(event);
  }

  warning(event: IEvent): Observable<void> {
    event.severity = SEVERITY.WARNING;
    return this.postEvent(event);
  }

  debug(event: IEvent): Observable<void> {
    event.severity = SEVERITY.DEBUG;
    return this.postEvent(event);
  }

  postEvent(event: IEvent): Observable<void> {
    const url = this.getUrl(API_URL.API_EVENTS);
    const MAX_DESCRIPTION_LENGTH = 255;
    const MAX_DETAIL_LENGTH = 1024;
    event.description = (event?.description?.length ?? 0) > MAX_DESCRIPTION_LENGTH ? event.description.slice(0, MAX_DESCRIPTION_LENGTH - 3) + "..." : event.description;
    event.detail = (event?.detail?.length ?? 0) > MAX_DETAIL_LENGTH ? event.detail.slice(0, MAX_DETAIL_LENGTH - 3) + "..." : event.detail;
    return this.http.post(url, {events: [event]})
        .pipe(catchError(this.handleErrorSilent.bind(this)));
  }

  postAnonymousDownloadEvent(uuid): Observable<void> {
    const url = this.getUrl(API_URL.API_EVENTS_DOWNLOAD);
    return this.http.post<void>(url, {uuid})
        .pipe(catchError(this.handleError.bind(this)));
  }

  postCopiedDownloadUrlEvent(subscription_uuid: string, filename: string): Observable<string> {
    const url = this.getUrl(API_URL.API_EVENTS_COPIED_DOWNLOAD_URL);
    return this.http.post<string>(url, {filename, subscription_uuid})
        .pipe(catchError(this.handleError.bind(this)));
  }

  searchEvents(query: IEventSearchRequest): Observable<IEventSearchResponse> {
    const url = this.getUrl('/api/events/viewer');
    return this.http.post(url, query)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getEventColumns(): Observable<IEventSearchResponse> {
    const url = this.getUrl('/api/events/columns');
    return this.http.get(url)
      .pipe(catchError(this.handleError.bind(this)
      ));
  }

  handleErrorSilent(errResponse: HttpErrorResponse | any): any {
    if ((errResponse instanceof HttpErrorResponse) && errResponse?.error?.status) {
        return new Promise((resolve, reject) => {
            this.translate.get(errResponse.error.status).subscribe({
              complete: () => resolve(null)
            });
        });
    }
    return throwError(errResponse.error || 'Server error');
  }

  handleError(errResponse: HttpErrorResponse | any): any {
    if ((errResponse instanceof HttpErrorResponse) && errResponse?.error?.status) {
        return new Promise((resolve, reject) => {
            this.translate.get(errResponse.error.status).subscribe(
                (translated) => reject(translated),
                (err) => reject('Server Error')
            );
        });
    }
    return throwError(errResponse.error || 'Server error');
  }
}
